/**
 * Single source of truth for contextual help content.
 *
 * Adding a new `<HelpHint>` or `<HelpPanel>` requires adding the corresponding
 * key here — the typed union enforces it.
 */

export type HelpKey =
  // Hardware tabs
  | 'hardware.boards'
  | 'hardware.sensors'
  | 'hardware.motors'
  // Boards
  | 'board.can_id'
  | 'board.board_type'
  | 'board.parent_board_id'
  // Sensors
  | 'sensor.sensor_type'
  | 'sensor.board_id'
  // Motors
  | 'motor.kind'
  | 'motor.board_id'
  | 'motor.channel_index'
  | 'motor.limit_switch_tag_ids'
  | 'motor.max_steps'
  | 'motor.max_speed'
  | 'motor.calibration.mark_home'
  | 'motor.calibration.set_position'
  | 'motor.calibration.soft_limits'
  // Actions
  | 'action.category'
  | 'action.subsystem_id'
  | 'action.on_demand'
  | 'action.instruction'
  | 'action.preconditions'
  | 'action.params'
  | 'action.state_updates'
  // Sequences
  | 'sequence.steps'
  | 'sequence.cycles'
  | 'sequence.on_failure'
  | 'sequence.layout'
  | 'sequence.max_runtime_ms'
  | 'sequence.subsystem_id'
  // Triggers
  | 'trigger.priority'
  | 'trigger.condition'
  | 'trigger.edge'
  | 'trigger.target'
  | 'trigger.preconditions'
  | 'trigger.on_failure'
  | 'trigger.debounce_ms'
  | 'trigger.notify_channels'
  // Steps
  | 'step.depends'
  | 'step.timeout_ms'
  | 'step.retry'
  | 'step.param_overrides'
  | 'step.on_success'
  | 'step.on_failure'
  // Preconditions
  | 'precondition.expression'
  | 'precondition.on_fail.skip'
  | 'precondition.on_fail.pause'
  | 'precondition.on_fail.retry'
  | 'precondition.on_fail.run_fix'
  // Subsystems
  | 'subsystem.name'
  | 'subsystem.color'
  // Admin
  | 'admin.tenant.archive'
  | 'admin.tenant.owner'
  | 'admin.user.super_admin'
  | 'admin.user.disabled'
  | 'admin.user.password_reset'
  // Members
  | 'member.role.viewer'
  | 'member.role.engineer'
  | 'member.role.workspace_admin'
  // Flow / boards canvases
  | 'program.canvas'
  | 'flow.edit_mode'
  | 'flow.add_step'
  | 'flow.depends_edge'
  | 'boards.parent_link'
  | 'boards.connector'
  | 'boards.can_edge'
  | 'boards.sensor_edge'
  // Building blocks — core concepts
  | 'concept.tag'
  | 'concept.channel'
  | 'concept.wire'
  | 'concept.step'
  // Logic-canvas blocks
  | 'logic.read_tag'
  | 'logic.write_tag'
  | 'logic.read_channel'
  | 'logic.and'
  | 'logic.or'
  | 'logic.not'
  | 'logic.add'
  | 'logic.compare'
  | 'logic.timer'
  | 'logic.pid'
  // Test panel — stepper / relay / LS (Sprint 2 + V2 limit-switch follow-ups)
  | 'test.stepper.ls_slot'
  | 'test.stepper.max_triggers'
  | 'test.stepper.skip_steps'
  | 'test.stepper.skip_count'
  | 'test.stepper.extra_steps'
  | 'test.stepper.encoder'
  | 'test.stepper.shuttle_cycles'
  | 'test.relay.ls_pin'
  | 'test.limit.bitmask'
  | 'test.tmc.ref_switch_flags'
  | 'test.tmc.dc_drive'
  | 'test.tmc.dc_duty'
  | 'test.tmc.dc_brake'
  | 'test.tmc.dc_ramp'
  // Alerts (Sprint 3 Phase 3G)
  | 'alerts.rule.name'
  | 'alerts.rule.severity'
  | 'alerts.rule.condition'
  | 'alerts.rule.threshold_value'
  | 'alerts.rule.clear_value'
  | 'alerts.rule.debounce_ms'
  | 'alerts.rule.quiet_hours'
  | 'alerts.rule.notification_channels'
  | 'alerts.rule.derived_from_channel'
  | 'alerts.event.ack_state'
  | 'alerts.event.transitions'
  | 'alerts.event.audit'
  // Notification channels (Sprint 3 Phase 3G-v)
  | 'channels.kind'
  | 'channels.smtp.recipients'
  | 'channels.smtp.host_port_user_pass'
  | 'channels.smtp.from_address'
  | 'channels.smtp.require_tls'
  | 'channels.webhook.url'
  | 'channels.webhook.signing_secret'
  | 'channels.webhook.extra_headers'
  | 'channels.workspace_smtp_defaults'
  // Firmware / OTA (Sprint 4)
  | 'firmware.version'
  | 'firmware.target_board_type'
  | 'firmware.binary'
  | 'firmware.signing'
  | 'ota.upgrade_now'
  | 'ota.run_status'
  | 'ota.rollback'
  | 'ota.motor_guard'
  // Workspace dashboard (Sprint 3 Phase 3H)
  | 'dashboard.severity_tiles'
  | 'dashboard.per_project'
  | 'dashboard.recent_triggers'
  // SSO (per-tenant)
  | 'sso.overview'
  | 'sso.sp_values'
  | 'sso.protocol'
  | 'sso.oidc.issuer'
  | 'sso.oidc.client_id'
  | 'sso.oidc.client_secret'
  | 'sso.saml.entity_id'
  | 'sso.saml.metadata_url'
  | 'sso.domains';

export interface HelpEntry {
  /** 1–3 sentence summary shown in the popover. */
  summary: string;
  /**
   * Optional longer-form markdown (paragraphs, bold, italics, lists,
   * inline code) rendered by the help markdown helper.
   */
  details?: string;
  /** Optional cross-references to related entries. */
  related?: HelpKey[];
}

export const HELP: Record<HelpKey, HelpEntry> = {
  // Hardware tabs ---------------------------------------------------
  'hardware.boards': {
    summary:
      'Boards are the microcontrollers wired into your machine. Each one has a CAN bus address and may have child sensors and motors attached.',
    details:
      'In MFVB source data the CAN address is called `espId`. The master board talks to every other board over CAN; this list is what tells the master who is on the bus.',
  },
  'hardware.sensors': {
    summary:
      'Sensors are inputs the master can read — temperature probes, limit switches, pressure transducers, etc. Each sensor lives on a parent board.',
  },
  'hardware.motors': {
    summary:
      'Motors are outputs the master drives — steppers, DC motors, belts. Each motor lives on a drive board and is wired to a specific channel on that board.',
  },

  // Board fields ----------------------------------------------------
  'board.can_id': {
    summary:
      'CAN bus address (0–255). The master uses this to route commands to this board. In MFVB source data this is called `espId`.',
    details:
      'Two boards on the same project must never share a `can_id`. Changing it requires reflashing the board firmware with the new id.',
    related: ['board.board_type'],
  },
  'board.board_type': {
    summary:
      'What hardware family this board is — e.g. drive board, sensor hub, master. Determines what kinds of children it can host.',
  },
  'board.parent_board_id': {
    summary:
      'Some boards plug into a parent (e.g. an expansion board on a drive board). Leave blank for top-level boards that talk directly to the master.',
  },

  // Sensors ---------------------------------------------------------
  'sensor.sensor_type': {
    summary:
      'What this sensor reports — e.g. temperature, limit, pressure, flow. Drives validation when sensors are referenced from actions and triggers.',
  },
  'sensor.board_id': {
    summary: 'The parent board this sensor is wired into. The master will read it via that board.',
  },

  // Motors ----------------------------------------------------------
  'motor.kind': {
    summary:
      'How the motor is driven: stepper, DC, or belt. The kind decides which calibration steps and instruction shapes apply.',
  },
  'motor.board_id': {
    summary:
      'The parent drive board this motor is wired into. Each drive board exposes a fixed number of motor channels.',
  },
  'motor.channel_index': {
    summary:
      'Which port on the parent board the motor is wired to. Drive boards typically have 4 motor ports indexed 0–3 (called `mIdx` in MFVB source).',
  },
  'motor.limit_switch_tag_ids': {
    summary:
      'Tags pointing at limit-switch sensors that protect this motor. The runtime stops the motor if any listed limit fires.',
  },
  'motor.max_steps': {
    summary:
      'Largest step count the motor is allowed to travel from home. Used as a hard upper bound across all moves.',
  },
  'motor.max_speed': {
    summary:
      'Largest commanded speed (steps per second for steppers, duty / RPM for DC) the runtime will let an action use.',
  },
  'motor.calibration.mark_home': {
    summary:
      'Sets the current physical position as step 0. Use after manually jogging the motor to its mechanical home.',
    details:
      'After calibration the runtime treats the current position as the origin. Soft limits and `set_position` are measured in steps relative to this point.',
  },
  'motor.calibration.set_position': {
    summary:
      'Override the current step counter without moving the motor. Use when you know exactly where the motor is sitting.',
  },
  'motor.calibration.soft_limits': {
    summary:
      'Min and max step values the motor is allowed to reach. The runtime refuses moves that would cross either bound.',
    details:
      'Soft limits act as a software-only safety net independent of any limit switches. They are applied **after** `mark_home`, so set them in the same coordinate system as the home position.',
    related: ['motor.calibration.mark_home', 'motor.limit_switch_tag_ids'],
  },

  // Actions ---------------------------------------------------------
  'action.category': {
    summary:
      'How this action is intended to be used — building block, recovery, setup, etc. Categories drive filtering and what shows up in pickers.',
  },
  'action.subsystem_id': {
    summary:
      'Which subsystem owns this action. Subsystems group related hardware so the canvas can lay them out together and the runtime can serialize work per subsystem.',
    related: ['subsystem.name'],
  },
  'action.on_demand': {
    summary:
      'When true, this action shows up in the operator-side "manual run" list. Leave false for actions only meant to be called from sequences and triggers.',
  },
  'action.instruction': {
    summary:
      'The unit of work this action performs — drive a stepper, read a sensor, set a tag, etc. Picking an instruction kind determines which params apply.',
    details:
      'Sensor reads capture one reading into a target tag: MLX temperature, RFID, HX711 load cell, ADC, and VL53 distance (`range_mm`). Their target-tag picker is scoped to the chosen sensor\'s tags, with a "show all tags" fallback; the VL53 read checks the sensor\'s model is actually `vl53`.',
  },
  'action.preconditions': {
    summary:
      'Checks that must pass before this action is allowed to run. Each check has an expression and a policy for what to do if it fails.',
    related: [
      'precondition.expression',
      'precondition.on_fail.skip',
      'precondition.on_fail.pause',
      'precondition.on_fail.retry',
      'precondition.on_fail.run_fix',
    ],
  },
  'action.params': {
    summary:
      'Inputs the caller can override at run time (e.g. a target step count). Default values come from this action; sequence steps may override per call.',
  },
  'action.state_updates': {
    summary:
      'Tag writes to apply when the action completes successfully. Used to record "this thing is now done" state visible to other actions.',
  },

  // Sequences -------------------------------------------------------
  'sequence.steps': {
    summary:
      'Ordered list of actions (or sub-sequences) this sequence runs. Steps can declare dependencies so independent steps run in parallel.',
    related: ['step.depends'],
  },
  'sequence.cycles': {
    summary:
      'How many times to repeat the whole sequence. 1 is the default; higher values are useful for batch jobs and burn-in.',
  },
  'sequence.on_failure': {
    summary:
      'What the runtime does if a step fails — abort, continue, or run a recovery sequence. Applies once per cycle.',
    details:
      '`abort` stops immediately. `continue` ignores the failure and runs the next step. `run_recovery` calls a named recovery sequence and then aborts the parent.',
  },
  'sequence.layout': {
    summary:
      'Saved x/y positions of each step on the canvas. The editor writes this so the canvas remembers your layout between sessions.',
  },
  'sequence.max_runtime_ms': {
    summary:
      'Hard upper bound on how long the sequence may run. The runtime aborts the sequence if it exceeds this duration.',
  },
  'sequence.subsystem_id': {
    summary:
      'Which subsystem owns this sequence. Used by the canvas for grouping and by the runtime for per-subsystem scheduling.',
    related: ['subsystem.name'],
  },

  // Triggers --------------------------------------------------------
  'trigger.priority': {
    summary:
      'Higher numbers fire first when multiple triggers want to run simultaneously. Reserve high priorities for safety triggers (e-stop, overheat).',
  },
  'trigger.condition': {
    summary:
      'What must be true for the trigger to fire. Build it as rows of tag → operator → value (e.g. `distance.range_mm < 100`) joined by a single AND/OR — no expression syntax to hand-type.',
    details:
      'Each row compares a tag to a value; the whole condition is one `${tag:NAME} <op> <value>` expression under the hood. Use **raw mode** for an advanced expression the builder does not cover — switching to raw keeps exactly what you typed and never rewrites a hand-authored condition. The runtime re-evaluates on every tag change in scope; pair with `edge` to fire on every match vs. only on transitions.',
    related: ['trigger.edge'],
  },
  'trigger.edge': {
    summary:
      'When the trigger fires relative to the condition: `rising edge` on false→true, `falling edge` on true→false, or `level high`/`level low` on every evaluation while the condition is true/false.',
  },
  'trigger.target': {
    summary:
      'What the trigger runs when it fires — typically a sequence or action. The target receives the trigger context as input.',
  },
  'trigger.preconditions': {
    summary:
      'Checks that gate the trigger in addition to its condition. Useful when the condition is a fast tag check but the side effects need extra safety guards.',
    related: ['action.preconditions'],
  },
  'trigger.on_failure': {
    summary:
      "What the runtime does if the trigger's target fails — abort the dispatch, continue, or escalate to a recovery sequence.",
  },
  'trigger.debounce_ms': {
    summary:
      'Minimum time between fires. After firing, the trigger is silenced for this many milliseconds even if the condition keeps matching.',
  },
  'trigger.notify_channels': {
    summary:
      'Where to send a notification when this trigger fires — email, Slack, in-app banner. Channels are configured per workspace.',
  },

  // Steps -----------------------------------------------------------
  'step.depends': {
    summary:
      'Other steps in this sequence that must finish before this step starts. Steps with no shared dependencies can run in parallel.',
  },
  'step.timeout_ms': {
    summary:
      'How long this individual step is allowed to run. The runtime aborts just this step (not the whole sequence) when it exceeds the limit.',
  },
  'step.retry': {
    summary:
      'What to do if this step fails: `none`, `linear` (fixed interval, capped attempts), or `exponential` (backoff up to a cap).',
    details:
      '`linear` retries every `interval_ms` up to `max_attempts` times. `exponential` doubles the wait each attempt starting from `initial_ms`, capped at `max_ms`, up to `max_attempts` total.',
  },
  'step.param_overrides': {
    summary:
      "Per-call values that replace the action's defaults for this step only. Useful when the same action is called twice with different inputs.",
    related: ['action.params'],
  },
  'step.on_success': {
    summary:
      'When this step finishes successfully (reaches Done), skip the selected downstream steps — a `skip_set` branch. Leave off for normal flow.',
    details:
      'Use this to take a shortcut on the happy path: e.g. a sensor-check step that, on success, skips the remedial steps that only matter when it fails. Only steps listed here are skipped; everything else runs per its dependencies.',
    related: ['step.on_failure', 'step.depends'],
  },
  'step.on_failure': {
    summary:
      'When this step is terminally Failed (after retries), skip the selected downstream steps — a `skip_set` branch. Leave off for normal flow.',
    details:
      'Use this to bypass steps that only make sense when this one succeeded, routing around the failure instead of aborting the whole sequence. Only steps listed here are skipped.',
    related: ['step.on_success', 'step.retry'],
  },

  // Preconditions ---------------------------------------------------
  'precondition.expression': {
    summary:
      'Boolean expression evaluated at runtime. Reference tags with `${tag:NAME}` and combine using `==`, `!=`, `<`, `>`, `<=`, `>=`.',
  },
  'precondition.on_fail.skip': {
    summary:
      'When the check fails, silently skip the gated work and continue. Use for checks that are nice-to-have but not required.',
  },
  'precondition.on_fail.pause': {
    summary:
      'When the check fails, pause the run and wait for an operator to acknowledge before continuing. Surfaces the failure to a human.',
  },
  'precondition.on_fail.retry': {
    summary:
      'When the check fails, wait `interval_ms` and retry up to `max_attempts` times before giving up.',
  },
  'precondition.on_fail.run_fix': {
    summary:
      'When the check fails, run a named "fix" action to attempt recovery, then re-check. Repeats up to `max_attempts` times.',
    details:
      'Pick a fix action that brings the system into a state where the check will pass. After each fix attempt the precondition is re-evaluated; the run continues only when it passes.',
  },

  // Subsystems ------------------------------------------------------
  'subsystem.name': {
    summary:
      'Short label that groups related hardware and logic together. Used everywhere subsystem filters appear (canvas, action picker, runtime metrics).',
  },
  'subsystem.color': {
    summary:
      'Color used on the canvas and in pickers to distinguish this subsystem at a glance. Pure cosmetic — has no runtime effect.',
  },

  // Admin -----------------------------------------------------------
  'admin.tenant.archive': {
    summary:
      'Archived workspaces are hidden from regular lists but their data is preserved. Reverse this from the same screen to restore visibility.',
    details:
      'Archive is a soft-delete: nothing is destroyed, billing pauses, and members lose access until the workspace is unarchived. Use **Delete** instead if you want the data gone permanently.',
  },
  'admin.tenant.owner': {
    summary:
      'Email of the user who will own this workspace. The owner gets workspace_admin access automatically; you can add more admins later.',
  },
  'admin.user.super_admin': {
    summary:
      'Grants platform-wide access to /admin/* pages. A super_admin can manage every workspace and every user.',
    details:
      'Stored as a Firebase custom claim and mirrored in the Firestore user doc. Toggling it requires a token refresh on the user side — they must sign out and back in for new claims to take effect.',
  },
  'admin.user.disabled': {
    summary:
      'Disabled users cannot sign in at all. Use this instead of deletion when you want to preserve their audit history.',
  },
  'admin.user.password_reset': {
    summary:
      'Sends a password reset email to the user via Firebase Auth. They keep their account; only their password changes.',
  },

  // Members ---------------------------------------------------------
  'member.role.viewer': {
    summary:
      'Read-only access to projects in this workspace. Viewers can browse hardware, actions, sequences, and runs but cannot edit or deploy.',
  },
  'member.role.engineer': {
    summary:
      'Can edit hardware, actions, sequences, triggers, and run them. Cannot manage workspace settings or invite members.',
  },
  'member.role.workspace_admin': {
    summary:
      'Full workspace access. Can manage members, billing, and all engineering surfaces. Required to invite or remove members.',
  },

  // Flow canvas / boards canvas ------------------------------------
  'program.canvas': {
    summary:
      'The Logic canvas is low-level control logic — wire sensors and inputs into compute blocks that drive outputs (relays, motors). It compiles to firmware and runs on the board itself, with no cloud round-trip. Reach for Logic when you need fast, always-on reactions; use Sequences for step-by-step automation that orchestrates actions in order or in parallel.',
    related: ['flow.edit_mode'],
  },
  'flow.edit_mode': {
    summary:
      'When edit mode is on, you can drag steps, change dependencies, and edit step details. Off, the canvas is read-only — useful for sharing without accidental edits.',
  },
  'flow.add_step': {
    summary:
      'Add a new step to the sequence. Pick an existing action or define a sub-sequence call. The new step starts with no dependencies.',
  },
  'flow.depends_edge': {
    summary:
      "An arrow from A → B means B depends on A and won't start until A finishes. Drag between two step handles to create the dependency.",
    related: ['step.depends'],
  },
  'boards.parent_link': {
    summary:
      'The boards canvas shows your hardware topology: master and child boards, the sensors attached to them, and the physical wires between channels. Edges come in three flavors — dashed indigo (CAN bus, derived from `parent_board_id`), solid emerald (sensor → board, derived from `sensor.board_id`), and colored solid (physical wires, by kind).',
    related: ['board.parent_board_id', 'boards.can_edge', 'boards.sensor_edge', 'boards.connector'],
  },
  'boards.connector': {
    summary:
      'Connectors mark how a child board plugs into its parent (port label, harness type). Hover or click to see connector details.',
  },
  'boards.can_edge': {
    summary:
      "Dashed indigo edges show CAN-bus relationships derived from each child board's `parent_board_id`. They are not physical wires — they indicate which boards report to which master over CAN.",
    related: ['board.parent_board_id'],
  },
  'boards.sensor_edge': {
    summary:
      "Solid emerald edges from a sensor node to its parent board are derived from the sensor's `board_id`. They show which board reads each sensor without requiring a Wire document.",
    related: ['sensor.board_id'],
  },

  // Building blocks — concepts --------------------------------------
  'concept.tag': {
    summary:
      'A Tag is a named value the whole system shares — the bus that connects boards, Logic, and Sequences. Its scope says what it is wired to: a board channel, a sensor reading, or a plain variable (system-wide or private to one program).',
    details:
      'Types: bool, int, float, string. A channel-scoped tag reads/drives a board I/O; a sensor-scoped tag is a live sensor reading; system/program tags just hold state. Read/Write Tag blocks and Sequences reference the same tags, which is how they talk to each other.',
  },
  'concept.channel': {
    summary:
      "A Channel is a single input or output pin on a board — the physical point a wire lands on. Types include digital/analog in and out, counter and PWM. Read Channel reads one directly; a channel-scoped Tag mirrors it by name.",
  },
  'concept.wire': {
    summary:
      "A Wire is a physical harness connection you draw by hand between two board pins (pin → pin). Unlike the sensor/motor links — which are derived from what's attached to a board — a wire is a real cable you record, colour-coded by kind (control, power, sensor, other).",
  },
  'concept.step': {
    summary:
      'A Step is one item in a Sequence — either an Action (a single machine instruction) or a call to another sub-sequence. Steps run on the Master.',
    details:
      'Order comes from the arrows between steps: an arrow A → B means B runs after A. Steps with no arrow between them start together, in parallel.',
    related: ['flow.depends_edge', 'sequence.steps'],
  },

  // Logic-canvas blocks ---------------------------------------------
  'logic.read_tag': {
    summary:
      'Reads a Tag and outputs its current value (port `value`). This is how logic pulls a live value in — a sensor reading or a board input via its channel-scoped tag, or a variable. The block shows the tag it uses and its binding.',
    related: ['concept.tag', 'logic.write_tag'],
  },
  'logic.write_tag': {
    summary:
      'Writes its input (port `value`) to a Tag. Bound to a channel tag it drives a board output (a relay, a motor); bound to a system/program tag it stores state other blocks or Sequences can read.',
    related: ['concept.tag', 'logic.read_tag'],
  },
  'logic.read_channel': {
    summary:
      'Reads a board channel directly and outputs its value (port `value`) — like Read Tag but pointed straight at a channel instead of a named tag.',
    related: ['concept.channel'],
  },
  'logic.and': {
    summary: 'Boolean AND: output is true only when both inputs `a` and `b` are true.',
  },
  'logic.or': {
    summary: 'Boolean OR: output is true when either input `a` or `b` is true.',
  },
  'logic.not': {
    summary: 'Boolean NOT: inverts the input — true becomes false and vice-versa.',
  },
  'logic.add': {
    summary: 'Adds two numbers: output = `a` + `b`.',
  },
  'logic.compare': {
    summary:
      'Compares two numbers with an operator (`>`, `<`, `=`, …) and outputs a boolean — e.g. is the pressure above a threshold.',
    details: 'Pick the operator on the block itself.',
  },
  'logic.timer': {
    summary:
      'While `enable` is held, `done` turns true after the set duration. Use it for debounces, delays, and timed steps.',
    details: 'Set the duration (in milliseconds) on the block itself.',
  },
  'logic.pid': {
    summary:
      'Closed-loop control that holds a value at a target — e.g. a temperature, pressure, or position.',
    details:
      'Inputs: `setpoint` = the target you want; `pv` (process value) = the current measured value (usually a sensor). Output `out` is the correction that drives the actuator. It is tuned by three gains you edit on the block: **Kp** (proportional — reacts to the current error, the main knob), **Ki** (integral — removes steady-state offset over time), **Kd** (derivative — dampens overshoot). Start with Kp only, then add Ki/Kd.',
  },

  // Test panel — stepper / relay / LS --------------------------------
  'test.stepper.ls_slot': {
    summary:
      'Sensor-stop slot. Blank = unconditional move. A number tells the slave to watch that bit of LIMITS_BITMASK during the move and abort when it asserts.',
    details:
      'PLCslayer V2 slot layout: **0–5** are TMC REFL/REFR endstops (axis *i* REFL = slot `2i`, REFR = slot `2i+1`), wired directly to the TMC5160 chip pins and read via IOIN every 10 ms; **≥ 6** are ISO inputs on TPM8866 accessory boards. The slot value is an index, not a GPIO number. Combine with **Trips** to abort after N rising edges instead of the first.\n\nThis path is the *software* sensor-stop. The TMC chip also has its own hardware SW_MODE stop (configured under the TMC control panel) that halts the ramp internally without firmware involvement — use that for safety-critical limits, this for choreographed homing.',
    related: ['test.stepper.max_triggers', 'test.tmc.ref_switch_flags'],
  },
  'test.stepper.max_triggers': {
    summary:
      'Number of rising edges on the LS slot to count before aborting the move. Default 1 = stop on first trigger.',
    details:
      'Useful for past-deadzone homing: a flag triggers the switch twice as it passes through, so a `max_triggers=2` move stops on the *second* edge to land past the switch. Has no effect when **LS slot** is blank.',
  },
  'test.stepper.skip_steps': {
    summary:
      'Blind phase: the stop sensor stays disarmed for the first N steps of the move. Default 0 = armed from the start. Has no effect when **LS slot** is blank.',
    details:
      'Use this to drive a flag off a switch it is already resting on before homing: the axis travels the first N steps ignoring the slot, then arms and watches for the next assertion. Without it the move would abort immediately on the already-pressed switch.',
    related: ['test.stepper.ls_slot', 'test.stepper.skip_count', 'test.stepper.extra_steps'],
  },
  'test.stepper.skip_count': {
    summary:
      'Once armed, ignore the first N trigger edges on the LS slot before honouring a stop. Default 0 = stop on the first armed edge. Has no effect when **LS slot** is blank.',
    details:
      'Complements **Skip steps**: that one disarms by distance, this one disarms by edge count. Counts edges *after* the blind phase ends, and is independent of **Trips** (which sets how many edges to count *toward* the stop once honouring begins). Use when a flag chatters or passes intermediate switches before the intended home edge.',
    related: ['test.stepper.ls_slot', 'test.stepper.skip_steps', 'test.stepper.max_triggers'],
  },
  'test.stepper.extra_steps': {
    summary:
      'After the stop condition fires, travel M more steps before halting (centering). Default 0 = stop immediately on trigger. Has no effect when **LS slot** is blank.',
    details:
      'Lets the axis coast past the switch edge to land in the middle of a slot or off the flag — e.g. a switch that asserts at the leading edge of a 200-step-wide vane, with `extra_steps=100` to centre on it. Direction follows the move; the extra travel is unconditional (the sensor is no longer consulted once it has fired).',
    related: ['test.stepper.ls_slot', 'test.stepper.skip_steps', 'test.stepper.skip_count'],
  },
  'test.stepper.encoder': {
    summary:
      'When on, the move uses the encoder feedback path (opcode `M`); when off, open-loop step counting (opcode `m`). TMC5160 moves (opcode `g`) ignore this.',
  },
  'test.stepper.shuttle_cycles': {
    summary:
      'Shuttle / ping-pong (opcode `s`): the slave runs +steps, then -steps, looped `cycles` times. Set 0 for infinite (until Stop).',
    details:
      "Re-uses the Move row's motor/steps/speed/accel. After each *back* leg the slave decrements `cycles_remaining`; on zero it clears RUNNING and the axis stops at its origin. Useful for burn-in, peristaltic priming, and conveyor agitation — anything you want to oscillate hands-off without authoring a sequence loop. **LS slot is ignored** for shuttle; use Move if you need sensor-stop homing.",
  },
  'test.relay.ls_pin': {
    summary:
      'Limit-switch pin that cuts the relay short if it fires during the timeout window. Use to protect actuators that should not run past a hard stop.',
    details:
      'The pin id is the LIMITS_BITMASK slot — same numbering as the stepper LS slot. Blank means "run for the full timeout regardless of any switch."',
    related: ['test.stepper.ls_slot'],
  },
  'test.limit.bitmask': {
    summary:
      'Reads LIMITS_BITMASK — every limit switch and ISO input the slave knows about. Slot N is bit N. Useful for confirming a switch is wired correctly before referencing it from a sequence.',
    related: ['test.stepper.ls_slot'],
  },
  'test.tmc.ref_switch_flags': {
    summary:
      'Writes the TMC5160 SW_MODE register so the chip stops the active ramp itself when REFL/REFR asserts. Independent of the software ls_slot path — TMC stops in microseconds without ESP involvement.',
    details:
      'Flag bits (per axis): `STOP_L` (bit 0), `STOP_R` (bit 1), `POL_L` (bit 2), `POL_R` (bit 3), `SOFT` (bit 4 = decelerate vs hard-stop). Use SOFT for high-speed loads to avoid lost steps; clear SOFT for emergency-stop semantics. REFL/REFR are wired directly to the TMC chip on PLCslayer V2 (chip pins LSL1/LSR1 etc.); IOIN exposes their state over SPI.',
    related: ['test.stepper.ls_slot'],
  },
  'test.tmc.dc_drive': {
    summary:
      'V2 only. The same TMC5160 chip can drive either a stepper or up to 5 A of DC motor across one H-bridge (coil-A pair). This card targets the DC case via opcode `d`.',
    details:
      "The slave writes the chip's XDIRECT register (REG 0x2D) with a signed 9-bit coil-A current; the bridges modulate to that target. Coil B is held at 0 so its leads can be left disconnected. The per-axis kind (stepper vs DC) is currently the operator's responsibility — sending `d` against a stepper-wired axis with the chip still in step-pulse mode writes XDIRECT but doesn't move the motor. A follow-up commit adds NVS-persisted per-axis kind config + slave-side rejection so misconfigured axes fail loudly.",
  },
  'test.tmc.dc_duty': {
    summary:
      'Signed -100..+100. Sign selects direction; magnitude is PWM duty percent. 0 stops the motor in the configured Stop mode (Coast or Short brake).',
  },
  'test.tmc.dc_brake': {
    summary:
      'How the motor stops at duty=0. **Coast** opens both bridges (high-Z; motor spins down on inertia). **Short brake** shorts the motor leads via the low-side MOSFETs — active braking, faster stop, but generates a current spike.',
    details:
      'Maps to PWMCONF.FREEWHEEL bits 21:20 on the TMC chip: 01 = high-Z coast, 10 = short low-side. Coast is safer for high-inertia loads; Short brake gives ~10×faster stop on light loads but can trip OT (overtemp) on hot motors. Brake mode is ignored while duty != 0.',
  },
  'test.tmc.dc_ramp': {
    summary:
      'Linear ramp time (ms) from the current duty to the new target. 0 = instant — useful for emergency stop / direction-reverse where you need the chip to switch immediately. Non-zero softens the transition; reduces current spike + mechanical shock.',
    details:
      'The slave worker ticks every 10 ms and advances `current_duty` by `(target - current) / (ramp_ms / 10)`. In-flight ramps are retargetable — a new duty command on the same axis reseeds the ramp from the current intermediate value. Stop commands (duty=0) ramp the same way; if you want a true e-stop, use ramp_ms=0 with Short brake.',
  },

  // Alerts (Sprint 3 Phase 3G) --------------------------------------
  'alerts.rule.name': {
    summary:
      'Short label that appears on every AlertEvent the rule fires. Pick something that reads well in a notification subject line.',
  },
  'alerts.rule.severity': {
    summary:
      '`info` / `warning` / `critical`. Drives sorting in the active-alerts panel, the workspace dashboard tiles, and (later) per-severity notification policies.',
  },
  'alerts.rule.condition': {
    summary:
      'What the rule watches. Threshold leaves compare one tag to a constant; All / Any branches combine children with AND / OR. Trees nest up to 4 levels.',
    details:
      'Threshold conditions compare a single tag value against a constant using `<`, `≤`, `>`, `≥`, `=`, `≠`. The evaluator subscribes to the live-tags stream, caches the most recent value per tag, and re-evaluates the whole tree on every tick of any child tag — so an All of (A > 10) AND (B > 20) only fires when both children currently hold. Unseeded leaves contribute `false`, so a rule stays cold until every leaf has reported at least once.\n\n**Clear semantics:** threshold leaves keep their per-leaf `clear_value` deadband (hysteresis). Compound roots use **symmetric clear** — the alert clears the instant the tree evaluates to false. Use a threshold root when you need hysteresis on a single number; wrap in All / Any only when truly combining multiple signals.\n\nExpression DSL (`${tag:foo} > ${tag:bar} + 5`) is parsed by zod but not yet evaluated; reserved for a follow-up.',
  },
  'alerts.rule.threshold_value': {
    summary:
      'The number the tag is compared against. Units come from the tag itself (e.g. °C if the tag is a temperature). The evaluator stops watching the tag once it exits the threshold band.',
  },
  'alerts.rule.clear_value': {
    summary:
      'Hysteresis floor. The rule re-arms only after the tag crosses back past this value. Leave blank for symmetric clear (same as the threshold).',
    details:
      'Example: threshold `> 80` with clear `70` means the alert fires when the tag rises above 80 and clears only when it falls below 70. Values between 70 and 80 are the deadband — no fire, no clear. Without `clear_value` the deadband is empty and the alert flaps at the threshold edge.',
    related: ['alerts.rule.debounce_ms'],
  },
  'alerts.rule.debounce_ms': {
    summary:
      'Condition must hold continuously for this long before firing. Use to filter noisy sensors. 0 fires on first sample that crosses the threshold.',
    related: ['alerts.rule.clear_value'],
  },
  'alerts.rule.quiet_hours': {
    summary:
      'Time window during which the rule still fires AlertEvents but the dispatcher does **not** send notifications. Per-rule, not per-recipient.',
    details:
      'Hours are local to the IANA timezone you set on the rule. Overnight windows (start > end, e.g. 22 → 7) wrap midnight. The evaluator and the active-alerts panel both ignore quiet hours — only the SMTP/webhook dispatcher honours them.',
    related: ['channels.workspace_smtp_defaults'],
  },
  'alerts.rule.notification_channels': {
    summary:
      'Channels the dispatcher fans out to when this rule fires. Manage channels under workspace → Channels.',
    related: ['channels.kind'],
  },
  'alerts.rule.derived_from_channel': {
    summary:
      "Tag this rule as auto-synthesised from a channel's `alarm_min`/`alarm_max` fields. Derived rules are read-only — edit the channel instead, or create a non-derived rule to override.",
  },
  'alerts.event.ack_state': {
    summary:
      'Full lifecycle: `unacked` → `acked` (someone is looking) → `assigned` (to a member) → `resolved` (cleared, by hand or auto). Any open state can reopen back to `unacked`.',
    related: ['alerts.event.transitions'],
  },
  'alerts.event.transitions': {
    summary:
      'Buttons show only the moves legal from the current state. Any project member can transition; notes are optional for ack/resolve/reopen and required for assign.',
    details:
      'Transitions: `unacked` → ack/assign/resolve; `acked` → assign/resolve/reopen; `assigned` → resolve/reopen; `resolved` → reopen. Every move writes an audit entry visible under **show audit**.',
    related: ['alerts.event.audit'],
  },
  'alerts.event.audit': {
    summary:
      'Append-only log of everything that happened to this event: triggered, cleared, notified, acked, assigned, resolved, reopened. Useful for postmortems.',
  },

  // Notification channels (Sprint 3 Phase 3G-v) ----------------------
  'channels.kind': {
    summary:
      'Channels are workspace-scoped and re-usable across rules. Two kinds: SMTP email (to one or more recipients) or HTTP webhook (HMAC-SHA256 signed POST).',
  },
  'channels.smtp.recipients': {
    summary:
      'Comma-separated `To:` addresses. At least one required. The dispatcher sends one mail per event with all recipients on the `To:` header.',
  },
  'channels.smtp.host_port_user_pass': {
    summary:
      'SMTP credentials. Leave blank to inherit from the workspace SMTP defaults; leave defaults blank to inherit from the cloud-api `SMTP_*` env vars.',
    details:
      'Resolution order on every send: **per-channel field → workspace SMTP defaults → cloud-api env var**. Password is write-only at the API boundary — the form starts blank on edit, and an empty submit preserves the previously stored secret. Use port `587` with STARTTLS for Office365/Gmail; port `465` triggers implicit TLS automatically.',
    related: ['channels.workspace_smtp_defaults', 'channels.smtp.require_tls'],
  },
  'channels.smtp.from_address': {
    summary:
      'Optional `From:` header. When blank, falls back to the workspace `SMTP_FROM` default, then to the SMTP user.',
  },
  'channels.smtp.require_tls': {
    summary:
      'Force STARTTLS on the connection. On for any production mailbox (Office365/Gmail require it). Off only when talking to a local relay you trust on a loopback.',
  },
  'channels.webhook.url': {
    summary:
      'HTTPS endpoint the dispatcher POSTs to. Each POST carries a JSON body and a `Rhia-Signature: t=<unix>,v1=<hex>` header. Validate the signature against your stored secret before trusting the payload.',
    details:
      'Signature recipe: `HMAC-SHA256(secret, "<unix>.<body>").hex`. The `t` value protects against replay — reject any timestamp older than your skew tolerance (Stripe uses 5 minutes; pick what makes sense for you).',
    related: ['channels.webhook.signing_secret'],
  },
  'channels.webhook.signing_secret': {
    summary:
      'Shared secret used to compute the `v1` signature. Write-only — reads return empty; an empty submit keeps the previous secret.',
    related: ['channels.webhook.url'],
  },
  'channels.webhook.extra_headers': {
    summary:
      'JSON object of extra HTTP headers sent on every POST. Useful for tenant ids, auth tokens, or routing hints your endpoint needs alongside the signature.',
  },
  'channels.workspace_smtp_defaults': {
    summary:
      'Fallback SMTP credentials used by any channel that leaves its `smtp_*` fields blank. Set once per workspace from the portal — no env access required.',
    details:
      'Resolution: per-channel → these defaults → cloud-api env vars. Password is write-only here too: blank on edit means "keep existing." Useful for single-tenant deployments that share one Office365/Gmail mailbox across every alert.',
    related: ['channels.smtp.host_port_user_pass'],
  },

  // Firmware / OTA (Sprint 4) ---------------------------------------
  'firmware.version': {
    summary:
      'Semver string (e.g. `0.2.0`) shown to the operator. Two firmware docs for the same `target_board_type` and the same version are allowed but confusing — bump the patch.',
  },
  'firmware.target_board_type': {
    summary:
      "Which board family this binary targets. The Upgrade dropdown on each board filters to candidates matching that board's `board_type`; the cloud-api refuses to start an OTA against a mismatched board.",
  },
  'firmware.binary': {
    summary:
      'The `.bin` produced by `cargo build` for the target. Max 16 MB. The server hashes it (SHA-256) and signs the hash on upload — the binary itself is stored verbatim.',
    related: ['firmware.signing'],
  },
  'firmware.signing': {
    summary:
      "Every upload is signed Ed25519 server-side using the cloud-api's configured key. The slave bootloader recomputes the SHA-256 after flashing and verifies it against the eFuse-burned public key before booting.",
    details:
      'Dev/bench supplies the private key via `OTA_SIGNING_PRIVATE_KEY_PEM`; production migrates to GCP KMS (deferred Sprint 4-H). The `signing_key_id` stored on every firmware doc lets historical signatures keep verifying after a key rotation. Uploads fail with a 503 if no key is configured — fail-loud rather than ship unsigned binaries.',
  },
  'ota.upgrade_now': {
    summary:
      'Starts an OTA run against this board. Confirm dialog warns about the 90 s auto-rollback. Refuses if any axis on the board has moved in the last 1.5 s.',
    related: ['ota.motor_guard', 'ota.rollback'],
  },
  'ota.run_status': {
    summary:
      'Lifecycle: `queued` → `transferring` → `verifying` → `rebooting` → `done`. Terminal failures are `failed` (mid-flash error), `rolled_back` (signature/heartbeat failure), or `cancelled` (operator).',
  },
  'ota.rollback': {
    summary:
      'If the new firmware does not call `mark_app_valid()` within 60 s of boot — or if the master sees no heartbeat from the new version within 90 s — the bootloader auto-reverts to the prior slot.',
    details:
      'The dual-partition layout (app0 / app1) makes rollback free: the bootloader just keeps pointing at the slot whose `revision_counter` was higher AND whose signature verifies. Nothing to manually un-do.',
  },
  'ota.motor_guard': {
    summary:
      'Refuses to start an OTA against a board if any of its axes has non-zero VACTUAL in the last 1.5 s. Stop the motors first.',
    details:
      'Source of truth is the motor-telemetry forwarder. The check is per-`can_id` so OTA against one slave does not block another slave on the same bus.',
  },

  // Workspace dashboard (Sprint 3 Phase 3H) -------------------------
  'dashboard.severity_tiles': {
    summary:
      'Counts of open events across every project in this workspace, grouped by severity. Refreshes every 30 s. Click a tile (in a future cut) to filter the lists below.',
  },
  'dashboard.per_project': {
    summary:
      'Per-project open-event counts. Click the project name to jump to its alerts page. Projects with no open events still appear so the dashboard shows the full lineup.',
  },
  'dashboard.recent_triggers': {
    summary:
      'Most recent 5 events fired across the workspace, newest first. Use as a heartbeat to spot a system spraying alerts before drilling into a project.',
  },

  // SSO (per-tenant) ------------------------------------------------
  'sso.overview': {
    summary:
      "Single sign-on lets this workspace's members sign in with their company identity provider — Microsoft/Entra (Office 365), Okta, Google Workspace, etc. Users are routed by their work-email domain.",
    details:
      'Set it up in four steps:\n\n' +
      '1. **Register RHIA Flow with your IdP.** For Microsoft, create an Entra app registration. Its redirect URI and SP entity id are the two values shown in the box below (the *"Give these to your IdP"* card).\n' +
      '2. **Create the connection here.** Microsoft/Office 365 uses the **OIDC** tab (Entra is an OIDC provider). Enter the issuer, client id, and client secret.\n' +
      '3. **Verify your email domain** in the Domains panel with a DNS TXT record, so `you@company.com` routes to this connection.\n' +
      '4. **Test:** sign in from an incognito window with a company email — you should be redirected to Microsoft. A first-time user lands in **pending approval** until an admin approves them.\n\n' +
      'Full walkthrough with the Microsoft-specific values and a verification checklist: [open the SSO Setup — Microsoft / Office 365 guide](/docs?doc=guide/sso-microsoft-o365).',
    related: ['sso.protocol', 'sso.domains'],
  },
  'sso.sp_values': {
    summary:
      'These identify RHIA Flow to your identity provider. Paste them into your IdP app: the Redirect/ACS URL is where the IdP sends users back after login; the SP Entity ID names this service.',
    details:
      'For a **Microsoft Entra** app registration:\n\n' +
      '- **Redirect URI** (platform *Web*) = the ACS / Redirect URL shown here.\n' +
      '- The **Entity ID** is only needed for SAML setups.\n\n' +
      'A redirect-URI mismatch is the most common failure (Microsoft error `AADSTS50011`) — copy it exactly.',
  },
  'sso.protocol': {
    summary:
      'Choose the protocol your IdP speaks. Microsoft/Entra (Office 365), Google Workspace, and Okta all support OIDC — pick OIDC unless your IdP admin specifically gives you SAML metadata.',
    details:
      '**OIDC** needs an issuer URL + client id + client secret. **SAML** needs the IdP entity id + metadata URL. One connection per workspace; saving replaces the previous one.',
  },
  'sso.oidc.issuer': {
    summary:
      "Your IdP's OIDC issuer URL (it serves /.well-known/openid-configuration). For Microsoft use the tenant-specific v2.0 endpoint.",
    details:
      'Microsoft/Entra issuer:\n\n' +
      '`https://login.microsoftonline.com/<tenant-id>/v2.0`\n\n' +
      '**Use your Directory (tenant) ID, not `/common/` or `/organizations/`** — Google Cloud Identity Platform checks that the token issuer matches exactly, and Entra stamps a tenant-specific issuer. `common` will fail with an issuer-mismatch error.',
  },
  'sso.oidc.client_id': {
    summary:
      'The client / application id your IdP issued for RHIA Flow. In Microsoft Entra this is the **Application (client) ID** on the app registration Overview page.',
  },
  'sso.oidc.client_secret': {
    summary:
      'The OIDC client secret from your IdP. In Entra: Certificates & secrets → New client secret → copy the secret **Value** (shown once).',
    details:
      '**Write-only.** For security the secret is never stored here — it goes straight to the auth broker. You must **re-enter it every time you save** the connection. Rotating the secret in Entra means creating a new one, then re-saving here.',
  },
  'sso.saml.entity_id': {
    summary:
      "Your IdP's SAML Entity ID (Issuer). For Microsoft, create an Entra **Enterprise Application** with SAML SSO and copy its Identifier. Prefer OIDC unless your IdP admin requires SAML.",
  },
  'sso.saml.metadata_url': {
    summary:
      "The IdP's SAML metadata URL — the signing certificate and SSO URL are read from it, so you never paste certificates by hand. Optional if you supply the entity id another way.",
  },
  'sso.domains': {
    summary:
      'Verify the email domains that should use this SSO connection. A user typing `you@company.com` at login is routed here only if `company.com` is verified.',
    details:
      '1. **Add domain** → you get a DNS **TXT** record value `rhia-sso-verify=<token>`.\n' +
      '2. Add it as a TXT record at the **domain apex** (`@`) with your DNS host.\n' +
      '3. Once DNS propagates, click **Verify**. That activates the connection.\n\n' +
      'Check it live: `dig +short TXT company.com` should show the record, then `\/api\/auth\/sso\/resolve?email=you@company.com` should return the provider id.',
  },
};

export function getHelp(key: HelpKey): HelpEntry {
  return HELP[key];
}
